import * as yup from 'yup';
import InfoIcon from '@mui/icons-material/Info';
import LoadingButton from '@mui/lab/LoadingButton';
import PermissionService from 'components/Common/PermissionService';
import React, { useEffect } from 'react';
import {
  Alert,
  Autocomplete, Divider, FormHelperText, Link, Skeleton, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import {
  HButton, HDialog, HDialogActions, HDialogContent, HDialogTitle,
} from 'BaseComponents';
import { organizationManagement } from 'utils/spaUrls';
import { productPerm } from 'utils/appConstants';
import { useCreateExportJob } from 'hooks/activationAPI';
import { useFetchOrgPartnerAccounts } from 'hooks/activationChannelsAPI';
import { useFetchQuestionActivationOptions } from 'api/questions';
import { useGetIdentityList } from 'api/OrganizationIdentity';
import { usePrepareTableForAssets } from 'api/questions/usePrepareTableForAssets';
import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';


const SelectRampIdModal = ({
  crId, question, dataContextRef, onClose,
}) => {
  const orgId = useSelector(state => state.session.user.primaryOrganization.organization.ID);
  const isTranscodingEnabled = PermissionService.isProductEnabled([productPerm.Transcoding]);
  const isCRActivationV1Enabled = PermissionService.isProductEnabled(
    [productPerm.cleanRoomActivationV1]);

  // Form validation schema
  const validationSchema = yup.object().shape({
    assetDisplayName: yup.string()
      .required()
      .min(3)
      .max(128)
      .matches(/^(?!\d+$)[a-zA-Z0-9_-]+$/, 'Table Name cannot be pure digital, contain spaces, or symbols besides _ and -')
      .label('Table Name'),
    rampIdColumn: yup.string().required().label('Ramp ID Column'),
    domainIdentifierID: yup.string()
      .when([], {
        is: () => isTranscodingEnabled && isCRActivationV1Enabled,
        then: (schema) => schema.required().matches(/^[A-Za-z0-9]{4}$/, 'Domain Identifier must be 4 alphanumeric characters').label('Domain Identifier'),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

  const {
    control, handleSubmit, setValue, formState: { errors },
  } = useForm({
    defaultValues: {
      assetDisplayName: question?.assetDisplayName?.length ? question?.assetDisplayName : question?.displayID ?? '',
      rampIdColumn: '',
      domainIdentifierID: '',
    },
    resolver: yupResolver(validationSchema),
  });

  const {
    isSuccess,
    isFetching,
    isError,
    data: questionActivationOptions,
  } = useFetchQuestionActivationOptions(
    crId, question?.ID,
  );

  const {
    isLoading: isFetchingIdentities,
    isSuccess: isFetchingIdentitiesSuccess,
    isError: isFetchingIdentitiesError,
    data: { domainIdentifiers = [] } = {},
  } = useGetIdentityList();

  useEffect(() => {
    if (questionActivationOptions) {
      setValue('rampIdColumn', question?.AssetRampIDColumnName?.length ? question?.AssetRampIDColumnName : questionActivationOptions?.identityField?.[0]?.name ?? '');
    }
  }, [questionActivationOptions, question, setValue]);

  useEffect(() => {
    if (
      isFetchingIdentitiesSuccess
      && domainIdentifiers.length > 0
      && isTranscodingEnabled
      && isCRActivationV1Enabled
    ) {
      setValue('domainIdentifierID', domainIdentifiers[0]?.domainIdentifierID || '');
    }
  }, [
    isFetchingIdentitiesSuccess,
    domainIdentifiers,
    isTranscodingEnabled,
    isCRActivationV1Enabled,
    setValue,
  ]);


  const {
    data: partnerAccount,
  } = useFetchOrgPartnerAccounts(orgId, {
    filters: [
      { attribute: 'Partner', values: ['LR-Activation'] },
    ],
    sortBy: {},
    limit: 1,
    offset: 0,
    search: '',
  });

  const {
    mutateAsync: prepareTableForAssets,
    isLoading: isPreparingTableForAssets,
  } = usePrepareTableForAssets();

  const {
    mutateAsync: createExportJob,
    isLoading: isCreatingExportJob,
  } = useCreateExportJob();

  // Handle modal close
  const handleClose = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  // Handle data submit
  const onSubmit = async (values) => {
    await prepareTableForAssets({
      cleanRoomId: crId,
      questionId: question?.ID,
      isUpdate: question?.isAsset,
      payload: {
        assetDisplayName: values.assetDisplayName ?? '',
        rampID: values.rampIdColumn ?? '',
        ...(isTranscodingEnabled && isCRActivationV1Enabled && {
          domainIdentifierID: values.domainIdentifierID ?? '',
        }),
      },
    });

    // create export job if question is not an asset
    if (!question.isAsset) {
      const partnerAccountID = partnerAccount?.data?.partnerAccounts[0]?.ID;
      if (partnerAccountID) {
        await createExportJob({
          cleanRoomID: crId,
          cleanRoomQuestionID: question?.ID,
          partnerAccountIDs: [partnerAccountID],
          exportParameters: [],
        });
      }
    }

    dataContextRef.current.refreshListData();
    handleClose();
  };

  return (
    <HDialog
      fullWidth
      maxWidth='xs'
      open
      onClose={handleClose}
      data-testid='select-rampId-modal'
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <HDialogTitle onClose={handleClose}>
          <Stack direction='row' justifyContent='space-between'>
            <Stack direction='row' spacing={2} alignItems='center'>
              <Typography variant='h5'>Prepare Your Table for Assets</Typography>
            </Stack>
          </Stack>
        </HDialogTitle>
        <Divider />
        <HDialogContent>
          {
            isFetching || isFetchingIdentities
              ? (
                <Stack spacing={4}>
                  <Skeleton variant='rectangular' height={40} />
                  <Skeleton variant='rectangular' height={40} />
                  {isTranscodingEnabled && isCRActivationV1Enabled && <Skeleton variant='rectangular' height={40} />}
                </Stack>
              )
              : (
                <>
                  {
                    isSuccess ? (
                      <Stack spacing={1}>
                        <>
                          <Controller
                            name='assetDisplayName'
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                id='input-table-name'
                                label='Table Name *'
                                fullWidth
                                size='small'
                                placeholder='Type table name'
                                variant='outlined'
                                error={errors?.assetDisplayName}
                              />
                            )}
                          />
                          <FormHelperText error>{errors?.assetDisplayName?.message}</FormHelperText>
                        </>
                        <>
                          <Controller
                            name='rampIdColumn'
                            control={control}
                            render={({ field }) => (
                              <Autocomplete
                                {...field}
                                size='small'
                                id='select-ramp-id-column'
                                data-testid='select-ramp-id-column'
                                options={
                                  questionActivationOptions?.identityField?.map(
                                    option => option.name,
                                  ) || []
                                }
                                value={field.value || ''}
                                getOptionLabel={option => option}
                                disableClearable
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label='Select Ramp ID Column *'
                                    size='small'
                                    variant='outlined'
                                    error={!!errors?.rampIdColumn}
                                  />
                                )}
                                onChange={(event, value) => field.onChange(value)}
                              />
                            )}
                          />
                          <FormHelperText error>{errors?.rampIdColumn?.message}</FormHelperText>
                        </>
                        {isTranscodingEnabled && isCRActivationV1Enabled && (
                          <>
                            <Stack direction='row' alignItems='center' justifyContent='center' spacing={1}>
                              <Controller
                                name='domainIdentifierID'
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    id='input-domain-identifier'
                                    label='RampID Domain Identifier *'
                                    fullWidth
                                    size='small'
                                    placeholder='Type RampID Domain Identifier'
                                    variant='outlined'
                                    disabled
                                    error={errors?.domainIdentifierID}
                                  />
                                )}
                              />
                              <Tooltip
                                title={(
                                  <Typography>
                                    Go to
                                    {' '}
                                    <Link href={organizationManagement.organization.identity.list} target='_self' rel='noopener noreferrer'>
                                      Identity
                                    </Link>
                                    {' '}
                                    to change the RampID Domain Identifier for your organization.
                                  </Typography>
                                )}
                                placement='right'
                              >
                                <span style={{ marginTop: '1rem' }}>
                                  <InfoIcon aria-label='More info about RampID Domain Identifier' />
                                </span>
                              </Tooltip>
                            </Stack>
                            <FormHelperText>4 alphanumeric characters</FormHelperText>
                            <FormHelperText error>
                              {errors?.domainIdentifierID?.message}
                            </FormHelperText>
                          </>
                        )}
                      </Stack>
                    ) : (
                      <>
                        {
                          isError && (
                            <Alert severity='error'>
                              <Typography variant='body2'>
                                Error fetching options, please try again later.
                              </Typography>
                            </Alert>
                          )
                        }
                        {
                          isFetchingIdentitiesError && (
                            <Alert severity='error'>
                              <Typography variant='body2'>
                                Error fetching identities, please try again later.
                              </Typography>
                            </Alert>
                          )
                        }
                      </>
                    )
                  }
                </>
              )
          }
        </HDialogContent>
        <Divider />
        <HDialogActions sx={{ my: 2 }}>
          <HButton
            variant='text'
            data-testid='cancel-button'
            onClick={handleClose}
          >
            Cancel
          </HButton>
          <LoadingButton
            variant='contained'
            color='primary'
            data-testid='share-button'
            loading={isPreparingTableForAssets || isCreatingExportJob}
            type='submit'
          >
            Confirm
          </LoadingButton>
        </HDialogActions>
      </form>
    </HDialog>
  );
};

export default SelectRampIdModal;
